import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import {
  statusBadgeAdminClass,
  type SupportMessage,
  type SupportTicket,
} from './types';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('es-CL', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function userInitials(name: string, email: string): string {
  const source = name.trim() || email;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function isStudentMessage(message: SupportMessage, studentId: string): boolean {
  return message.author.id === studentId;
}

export function SoporteTicketPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const [closeOnSend, setCloseOnSend] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['student', 'support', 'tickets', id],
    queryFn: () => api<SupportTicket>(`/student/support/tickets/${id}`),
    enabled: Boolean(id),
  });

  const replyMutation = useMutation({
    mutationFn: (body: string) =>
      api<SupportTicket>(`/student/support/tickets/${id}/reply`, {
        method: 'POST',
        body: { body },
      }),
    onSuccess: async () => {
      setReply('');
      await queryClient.invalidateQueries({ queryKey: ['student', 'support', 'tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['student', 'support', 'tickets', id] });
    },
  });

  const closeMutation = useMutation({
    mutationFn: () =>
      api<SupportTicket>(`/student/support/tickets/${id}/close`, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['student', 'support', 'tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['student', 'support', 'tickets', id] });
    },
  });

  const messages = ticket?.messages ?? [];
  const messageCount = messages.length;
  const lastMessageId = messages.at(-1)?.id;

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread || messageCount === 0) return;

    const scrollToBottom = () => {
      thread.scrollTop = thread.scrollHeight;
    };

    scrollToBottom();
    const frame = requestAnimationFrame(scrollToBottom);
    return () => cancelAnimationFrame(frame);
  }, [id, messageCount, lastMessageId]);

  async function handleReply(e: FormEvent) {
    e.preventDefault();
    if (!reply.trim()) return;
    await replyMutation.mutateAsync(reply.trim());
    if (closeOnSend) {
      await closeMutation.mutateAsync();
      setCloseOnSend(false);
    }
  }

  if (isLoading || !ticket) {
    return (
      <div className="lms-support-ticket lms-site-admin-hub-page">
        <div className="support-ticket-loading">
          <Loader label="Cargando ticket…" />
        </div>
      </div>
    );
  }

  const canReply = ticket.status !== 'cerrado';
  const canClose = ticket.status !== 'cerrado';
  const assigneeLabel =
    ticket.assignee?.name?.trim() || 'Equipo de Soporte Académico / LMS';
  const studentName = ticket.user.name.trim() || ticket.user.email.split('@')[0];

  return (
    <div className="lms-support-ticket lms-site-admin-hub-page">
      <div className="support-ticket-canvas">
        <header className="support-ticket-header font-sans">
          <HubBackLink to="/soporte" label="Volver a mesa de ayuda" />
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
              Ticket #{ticket.number}: {ticket.subject}
            </h1>
          </div>
        </header>

        <div className="support-ticket-layout">
          <section className="support-ticket-main">
            <div className="support-ticket-thread-card">
              <div className="support-ticket-thread-card__head">
                <div className="support-ticket-thread-card__heading">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    forum
                  </span>
                  <h2>Historial de Comunicación</h2>
                </div>
                <span className="support-ticket-count-badge">
                  {messageCount} {messageCount === 1 ? 'mensaje' : 'mensajes'}
                </span>
              </div>

              <div ref={threadRef} className="support-ticket-timeline">
                {messages.map((message, index) => {
                  const isMine = isStudentMessage(message, ticket.user.id);
                  const isFirst = index === 0;

                  if (isMine) {
                    return (
                      <article key={message.id} className="support-ticket-message support-ticket-message--mine">
                        <div className="support-ticket-message__avatar" aria-hidden="true">
                          {userInitials(message.author.name, message.author.email)}
                        </div>
                        <div className="support-ticket-message__content">
                          <div className="support-ticket-message__meta">
                            <div className="support-ticket-message__author-row">
                              <span className="support-ticket-message__author">Tú</span>
                              <span className="support-ticket-message__email">
                                &lt;{message.author.email}&gt;
                              </span>
                              {isFirst ? (
                                <span className="support-ticket-tag support-ticket-tag--mine">
                                  Mi consulta
                                </span>
                              ) : null}
                            </div>
                            <time dateTime={message.createdAt}>{formatDate(message.createdAt)}</time>
                          </div>
                          <div className="support-ticket-message__bubble support-ticket-message__bubble--mine">
                            {message.body}
                          </div>
                        </div>
                      </article>
                    );
                  }

                  return (
                    <article
                      key={message.id}
                      className="support-ticket-message support-ticket-message--staff"
                    >
                      <div
                        className="support-ticket-message__avatar support-ticket-message__avatar--staff"
                        aria-hidden="true"
                      >
                        <span className="material-symbols-outlined">support_agent</span>
                      </div>
                      <div className="support-ticket-message__content">
                        <div className="support-ticket-message__meta">
                          <div className="support-ticket-message__author-row">
                            <span className="support-ticket-message__author support-ticket-message__author--staff">
                              Equipo de Soporte
                            </span>
                            <span className="support-ticket-tag support-ticket-tag--staff">
                              Soporte Oficial
                            </span>
                          </div>
                          <time dateTime={message.createdAt}>{formatDate(message.createdAt)}</time>
                        </div>
                        <div className="support-ticket-message__bubble support-ticket-message__bubble--staff">
                          {message.body}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            {canReply ? (
              <form onSubmit={handleReply} className="support-ticket-composer">
                <div className="support-ticket-composer__head">
                  <div className="support-ticket-composer__heading">
                    <span className="material-symbols-outlined" aria-hidden="true">
                      edit_note
                    </span>
                    <h2>Tu respuesta</h2>
                  </div>
                  <span className="support-ticket-composer__hint">Formato Markdown disponible</span>
                </div>

                <div className="support-ticket-composer__box">
                  <div className="support-ticket-composer__toolbar" aria-hidden="true">
                    <button type="button" className="support-ticket-composer__tool" tabIndex={-1}>
                      <span className="material-symbols-outlined">format_bold</span>
                    </button>
                    <button type="button" className="support-ticket-composer__tool" tabIndex={-1}>
                      <span className="material-symbols-outlined">format_italic</span>
                    </button>
                    <button type="button" className="support-ticket-composer__tool" tabIndex={-1}>
                      <span className="material-symbols-outlined">link</span>
                    </button>
                    <button type="button" className="support-ticket-composer__tool" tabIndex={-1}>
                      <span className="material-symbols-outlined">format_list_bulleted</span>
                    </button>
                    <button type="button" className="support-ticket-composer__tool" tabIndex={-1}>
                      <span className="material-symbols-outlined">code</span>
                    </button>
                    <span className="support-ticket-composer__divider" />
                    <button type="button" className="support-ticket-composer__attach" tabIndex={-1}>
                      <span className="material-symbols-outlined">attach_file</span>
                      <span>Adjuntar archivo</span>
                    </button>
                  </div>
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={4}
                    required
                    placeholder="Escribe tu respuesta aquí..."
                    className="support-ticket-composer__textarea"
                  />
                </div>

                <div className="support-ticket-composer__actions">
                  <div className="support-ticket-composer__options">
                    <label className="support-ticket-composer__checkbox">
                      <input
                        type="checkbox"
                        checked={closeOnSend}
                        onChange={(e) => setCloseOnSend(e.target.checked)}
                      />
                      <span>Dar por resuelto y cerrar ticket al enviar</span>
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={replyMutation.isPending || closeMutation.isPending}
                    className="support-ticket-composer__submit"
                  >
                    <span>Enviar respuesta</span>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      send
                    </span>
                  </button>
                </div>
              </form>
            ) : (
              <p className="support-ticket-closed-note">
                Este ticket está cerrado.
                {ticket.closedAt ? ` Cerrado el ${formatDate(ticket.closedAt)}.` : ''}
              </p>
            )}
          </section>

          <aside className="support-ticket-sidebar">
            {canClose ? (
              <div className="support-ticket-close-card">
                <div className="support-ticket-close-card__head">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    task_alt
                  </span>
                  <h3>Cerrar ticket</h3>
                </div>
                <p>
                  Si el equipo de soporte ya resolvió tu duda o problema, puedes dar por cerrado este
                  ticket.
                </p>
                <button
                  type="button"
                  onClick={() => closeMutation.mutate()}
                  disabled={closeMutation.isPending}
                  className="support-ticket-close-btn"
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    lock
                  </span>
                  <span>Cerrar ticket</span>
                </button>
              </div>
            ) : null}

            <div className="support-ticket-details">
              <div className="support-ticket-details__head">
                <h3>Detalles del Caso</h3>
                <span className="material-symbols-outlined" aria-hidden="true">
                  info
                </span>
              </div>

              <dl className="support-ticket-details__list">
                <div className="support-ticket-details__row">
                  <dt>ID de Referencia</dt>
                  <dd>#{ticket.number}</dd>
                </div>
                <div className="support-ticket-details__row">
                  <dt>Estado</dt>
                  <dd>
                    <span className={statusBadgeAdminClass(ticket.status)}>{ticket.statusLabel}</span>
                  </dd>
                </div>
                <div className="support-ticket-details__row">
                  <dt>Categoría</dt>
                  <dd>{ticket.categoryLabel}</dd>
                </div>
                {ticket.course ? (
                  <div className="support-ticket-details__row">
                    <dt>Curso</dt>
                    <dd>{ticket.course.name}</dd>
                  </div>
                ) : null}
                <div className="support-ticket-details__row">
                  <dt>Asignado a</dt>
                  <dd className="support-ticket-details__assignee">
                    <span className="material-symbols-outlined" aria-hidden="true">
                      support_agent
                    </span>
                    <span>{assigneeLabel}</span>
                  </dd>
                </div>
                <div className="support-ticket-details__requester">
                  <dt>Solicitante</dt>
                  <dd>
                    <div className="support-ticket-requester">
                      <div className="support-ticket-requester__avatar" aria-hidden="true">
                        {userInitials(ticket.user.name, ticket.user.email)}
                      </div>
                      <div className="support-ticket-requester__info">
                        <div className="support-ticket-requester__name-row">
                          <span>Tú ({studentName})</span>
                          <span className="support-ticket-tag support-ticket-tag--student">
                            Estudiante
                          </span>
                        </div>
                        <span className="support-ticket-requester__email">{ticket.user.email}</span>
                      </div>
                    </div>
                  </dd>
                </div>
                <div className="support-ticket-details__row">
                  <dt>Fecha de creación</dt>
                  <dd>{formatDate(ticket.createdAt)}</dd>
                </div>
                <div className="support-ticket-details__row">
                  <dt>Última actividad</dt>
                  <dd>{formatDate(ticket.updatedAt)}</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
